/**
 * Idle-based sign-out with a "you're about to be signed out" prompt.
 *
 * Server session is workday-length (better-auth: expiresIn=8h, updateAge=5m).
 * That answers "I left my tab open and got logged out" — but it doesn't cover
 * the security side: a user who walks away should still be signed out.
 *
 * This component is the client-side enforcement of that policy. After
 * IDLE_TIMEOUT_MS of no mouse/keyboard/touch activity, the prompt fires
 * with a PROMPT_BEFORE_IDLE_MS countdown. If the user doesn't click
 * "Stay signed in" before the countdown expires, we call `signOut()` and
 * bounce to /login with `?next=` so they land back where they were after
 * re-auth.
 *
 * `crossTab: true` makes activity in any Clockwise tab reset the timer
 * in all of them — so two-tab workflows don't false-trigger the prompt.
 *
 * Running-timer exception: while a timer is actively running the user is
 * mid-work even if they're not touching this tab (a call, another window,
 * reading). Two things happen for as long as a timer runs:
 *   1. The idle timer is PAUSED, so the inactivity prompt never fires and
 *      the user is never signed out mid-timer.
 *   2. A `getSession()` heartbeat pings `/api/auth/*`, which is the path that
 *      forwards better-auth's refreshed Set-Cookie, so the server session
 *      keeps sliding forward (the ordinary domain-API poll on /api/timers
 *      reads the session but does NOT re-issue the cookie).
 * When no timer is running we restart the idle timer and the walk-away
 * policy applies as normal. "Running" here is the single running timer —
 * paused timers mean the user stepped away from timing, so idle logout
 * should still apply to them.
 */
import { useEffect, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getSession, signOut } from '@/lib/auth-client';
import { useTimers } from '@/lib/timer-hooks';

/**
 * Total idle duration before forced sign-out. Overridable via
 * `VITE_IDLE_TIMEOUT_MS` so the e2e suite can run with seconds-long
 * windows instead of waiting 8 hours per spec.
 */
const IDLE_TIMEOUT_MS =
  devSessionOverride('e2e:idleTimeoutMs') ??
  parsePositiveInt(import.meta.env.VITE_IDLE_TIMEOUT_MS, 8 * 60 * 60 * 1000);
/**
 * Window before forced sign-out during which the warning is shown.
 * Overridable via `VITE_IDLE_PROMPT_MS` for the same reason.
 */
const PROMPT_BEFORE_IDLE_MS =
  devSessionOverride('e2e:idlePromptMs') ??
  parsePositiveInt(import.meta.env.VITE_IDLE_PROMPT_MS, 2 * 60 * 1000);

/**
 * Per-tab override of the idle window, for e2e only.
 *
 * The `VITE_IDLE_*_MS` vars are baked at dev-server start, which makes them a
 * SUITE-wide setting — and the suite wants two incompatible values. The idle
 * specs need a ~3s window to observe prompt-then-sign-out; every page-driven
 * UI spec needs a long one or it gets signed out mid-flow. Running the whole
 * suite green therefore required hand-editing apps/e2e/.env.local between
 * runs, and whichever value was in place silently failed the other group.
 *
 * A sessionStorage read makes it per-tab: specs that need the compressed
 * window set it via `page.addInitScript` (see helpers/idle.ts) and everything
 * else keeps the relaxed env default. Gated on `import.meta.env.DEV` so it
 * cannot be reached from a production build.
 */
function devSessionOverride(key: string): number | undefined {
  if (!import.meta.env.DEV) return undefined;
  try {
    return parsePositiveIntOrUndefined(window.sessionStorage.getItem(key));
  } catch {
    // sessionStorage throws when storage is disabled/partitioned. This is a
    // dev-only test affordance, so falling back to the env default is the
    // correct behavior, not a swallowed failure.
    return undefined;
  }
}

function parsePositiveIntOrUndefined(raw: string | null): number | undefined {
  if (!raw) return undefined;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}
/** Countdown re-render cadence while the warning is open. */
const COUNTDOWN_TICK_MS = 1000;
/**
 * How often to ping `getSession()` while a timer is running, to keep the
 * server session sliding forward. Comfortably under the 5m `updateAge` /
 * 8h `expiresIn` window; better-auth only re-issues the cookie once the
 * session is older than `updateAge`, so most pings are cheap no-ops.
 */
const SESSION_KEEPALIVE_MS = 5 * 60 * 1000;

function parsePositiveInt(raw: string | undefined, fallback: number): number {
  if (!raw) return fallback;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

function formatMmSs(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

async function signOutAndBounce(): Promise<void> {
  // `signOut` clears the cookie server-side; the final hop is a hard
  // navigation so any in-flight TanStack queries get torn down with the
  // page rather than racing the redirect.
  try {
    await signOut();
  } finally {
    const next = window.location.pathname + window.location.search;
    window.location.assign(`/login?next=${encodeURIComponent(next)}`);
  }
}

export function SessionIdleWarning(): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [remainingMs, setRemainingMs] = useState(PROMPT_BEFORE_IDLE_MS);

  // A running timer means the user is mid-work; suppress idle sign-out and
  // keep the server session alive for as long as it runs. `useTimers` is a
  // shared observer on the open-timers query (deduped with the TimerHud's),
  // gated on `timer.view` — users without timers get `null` and the normal
  // walk-away policy.
  const { running } = useTimers();
  const isTimerRunning = Boolean(running);

  const { activate, getRemainingTime, pause, start } = useIdleTimer({
    timeout: IDLE_TIMEOUT_MS,
    promptBeforeIdle: PROMPT_BEFORE_IDLE_MS,
    // While the prompt is showing, mouse moves shouldn't silently dismiss
    // it — the user must explicitly choose. react-idle-timer's prompt
    // semantics already enforce this; activity only resets after we call
    // `activate()`. `crossTab` keeps multi-tab workflows in sync.
    crossTab: true,
    onPrompt: () => {
      setRemainingMs(PROMPT_BEFORE_IDLE_MS);
      setOpen(true);
    },
    onIdle: () => {
      setOpen(false);
      void signOutAndBounce();
    },
    onActive: () => setOpen(false),
  });

  // Tick the countdown while the prompt is visible. The interval is gated
  // on `open` so we don't burn a timer the rest of the time.
  useEffect(() => {
    if (!open) return;
    const id = setInterval(() => {
      setRemainingMs(getRemainingTime());
    }, COUNTDOWN_TICK_MS);
    return () => clearInterval(id);
  }, [open, getRemainingTime]);

  // Suppress idle sign-out while a timer runs: pause the idle timer (freezes
  // the countdown, so onPrompt/onIdle can't fire) and dismiss any prompt that
  // was already showing. When the timer stops, `start()` restores the full
  // idle window so the walk-away policy resumes from a clean slate.
  useEffect(() => {
    if (isTimerRunning) {
      setOpen(false);
      pause();
    } else {
      start();
    }
  }, [isTimerRunning, pause, start]);

  // Keep the server session sliding while a timer runs. `getSession()` hits
  // `/api/auth/get-session`, the one path that forwards better-auth's
  // refreshed cookie (the /api/timers poll reads the session but doesn't
  // re-issue it). Fire immediately on start, then on an interval.
  useEffect(() => {
    if (!isTimerRunning) return;
    const ping = (): void => {
      void getSession();
    };
    ping();
    const id = setInterval(ping, SESSION_KEEPALIVE_MS);
    return () => clearInterval(id);
  }, [isTimerRunning]);

  const handleStay = (): void => {
    activate();
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      // The dialog is intentionally non-dismissible by ESC / overlay click:
      // the user must answer "Stay signed in" or "Sign out". Passing a
      // no-op onOpenChange keeps Radix from closing on those events.
      onOpenChange={() => {}}
    >
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Still there?</DialogTitle>
          <DialogDescription>
            You'll be signed out in <span className="font-mono">{formatMmSs(remainingMs)}</span> due
            to inactivity.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => void signOutAndBounce()}>
            Sign out
          </Button>
          <Button onClick={handleStay}>Stay signed in</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
